import { Box, Divider, Group, Input, SegmentedControl, Text } from '@byecode/ui'
import { getAssetUrl } from '@lighthouse/assets'
import { ARRANGEMENT, type Field, IMAGE_SHOW_TYPE, IMAGE_SOURCE_TYPE, type PageAbstract, SHAPE } from '@lighthouse/core'
import {
    getInputVariableOption,
    ListItem4ByecodeUi,
    RATIO_OPTIONS,
    SelectMode,
    useAtomAction,
    useAtomData,
    VariableSelect
} from '@lighthouse/shared'
import React, { useCallback, useDeferredValue, useMemo, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import styled from 'styled-components'

import { availableFieldBlocksAtom, blockHighlightAtom, lastPageOfStackAtom } from '@/atoms/page/state'
import { AnchorSetting } from '@/components/AnchorSetting'
import { EventList } from '@/components/EventList'
import { getBlockAndPageDesignLimit, PositionSetting } from '@/components/StyleSetting'
import AnimationSetting from '@/components/StyleSetting/AnimationSetting'
import { GapSizeInput } from '@/components/StyleSetting/AutoLayoutSetting/GapSizeInput'
import { SizeSetting } from '@/components/StyleSetting/SizeSetting'
import { RectSizeInput } from '@/components/StyleSetting/SizeSetting/RectSizeInput'
import { VisibilitySetting } from '@/components/VisibilitySetting'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'
import { getPexelsList } from '@/services'

import { FieldSelect as Select } from '../../FieldSelect'
import { BlockConfigureSegmentedControl } from '../Common/BlockConfigureSegmentedControl'
import { CursorSetting } from '../Common/CursorSetting'
import { SwitchControl } from '../Common/SwitchControl'
import type { BlockSettingsSegmentedEnum } from '../constants'
import {
    ALIGN_CONTROL,
    BLOCK_IMAGE_SOURCES,
    BLOCK_SETTINGS_SEGMENTED_ENUM,
    DIRECTION_OPTIONS,
    IMAGE_FIT_OPTIONS,
    SHAPE_OPTIONS
} from '../constants'
import ImageUploadList from './ImageUploadList'

export const maxFileNum = 10

const SCxStyleContainer = styled.div`
    display: flex;
    flex-direction: column;
    /* gap: 8px; */
`
const FIELD: Field = { type: 'file', id: 'file', dsId: '', name: '图片', innerType: 'ARRAY' }
const multipleSvg = getAssetUrl('common', 'multiple_picture.svg')
const gridSvg = getAssetUrl('common', 'grid_picture.svg')
const singleSvg = getAssetUrl('common', 'single_picture.svg')

const IMAGE_SHOW_OPTIONS = [
    { label: '单图', value: 'single', icon: multipleSvg },
    { label: '宫格', value: 'grid', icon: gridSvg },
    { label: '平铺多图', value: 'multiple', icon: singleSvg }
]

// const CUSTOM_VIEW_RECORD_SIZE = BLOCK_SIZE_OPTIONS.filter(item => item.value !== 'xs')

// const COLS_OPTIONS = Array.from({ length: 12 }).map((_, i) => ({
//     label: `${i + 1} 列`,
//     value: (i + 1).toString()
// }))
// const ROWS_OPTIONS = Array.from({ length: 12 }).map((_, i) => ({
//     label: `${i + 1} 行`,
//     value: (i + 1).toString()
// }))

export const BlockImageSettings: React.FC<{ allPages: PageAbstract[]; blockId: string }> = ({ blockId, allPages = [] }) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const disabledWithVersion = useIsDisabledWithVersion()
    const styleProps = getBlockAndPageDesignLimit('image')
    const { control, register, watch, setValue } = useFormContext()
    const [shape, ratio, carousel, sourceType, isPreview, autoPlay, showType = IMAGE_SHOW_TYPE.single, direction] = watch([
        'shape',
        'ratio',
        'carousel',
        'sourceType',
        'isPreview',
        'autoPlay',
        'showType',
        'direction'
    ])

    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => s?.pageId || '', [])
    )

    const { run: setBlockHighlight } = useAtomAction(blockHighlightAtom)
    const handleMouseEnter = useCallback(
        (id: string) => {
            setBlockHighlight([id])
            document.querySelector(`div[data-node-id="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        },
        [setBlockHighlight]
    )
    const handleMouseLeave = useCallback(() => {
        setBlockHighlight([])
    }, [setBlockHighlight])

    const availableFieldBlocksInfo = useDeferredValue(useAtomData(availableFieldBlocksAtom))
    const inputOption = useMemo(() => {
        if (availableFieldBlocksInfo.length === 0) {
            return undefined
        }
        return getInputVariableOption(availableFieldBlocksInfo, dataSourceList, {
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave
        })
    }, [availableFieldBlocksInfo, dataSourceList, handleMouseEnter, handleMouseLeave])
    const { customViewOption } = useVariableCustomViewOption()
    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })
    const userOption = useGetUserRecordOptions()

    // 原图比例仅限于矩形使用且非swiper， 1:1 只有圆形和正方形可使用
    const newRatioOptions = useMemo(() => {
        if (carousel) {
            return RATIO_OPTIONS
        }
        return shape === SHAPE.rectangle ? RATIO_OPTIONS.filter(item => !item.disable) : RATIO_OPTIONS
    }, [shape, carousel])

    const [currentTab, setCurrentTab] = useState<BlockSettingsSegmentedEnum>(BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN)

    const isSingle = showType === IMAGE_SHOW_TYPE.single
    const isMultiple = showType === IMAGE_SHOW_TYPE.multiple
    const isGrid = showType === IMAGE_SHOW_TYPE.grid

    return (
        <>
            <BlockConfigureSegmentedControl value={currentTab} isInterAction onChange={setCurrentTab} />

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN && (
                <>
                    <Group label="类型">
                        <Box px={8}>
                            <Controller
                                name="showType"
                                control={control}
                                render={({ field }) => {
                                    return (
                                        <SelectMode
                                            options={IMAGE_SHOW_OPTIONS}
                                            itemWidth="100%"
                                            styles={{
                                                image: {
                                                    height: 82,
                                                    // padding: '20px 0px 0px 0px',
                                                    borderRadius: '8px',
                                                    // width: 158,
                                                    // height: 68,
                                                    outline: 'var(--color-gray-200) solid 1px',
                                                    background: 'var(--color-gray-100)'
                                                },
                                                root: {
                                                    width: '100%',
                                                    gap: '16px',
                                                    marginTop: 8
                                                }
                                            }}
                                            value={field.value ?? IMAGE_SHOW_TYPE.single}
                                            onChange={val => {
                                                if (val === IMAGE_SHOW_TYPE.grid) {
                                                    setValue('radius', '6')
                                                    setValue('shape', SHAPE.square)
                                                    setValue('ratio', '7')

                                                    setValue('arrangement', ARRANGEMENT.fixed)
                                                    setValue('cols', 3)
                                                    setValue('rows', 3)
                                                }
                                                field.onChange(val)
                                            }}
                                        />
                                    )
                                }}
                            />
                        </Box>
                    </Group>
                    <Divider color="var(--color-gray-200)" />
                    <PositionSetting {...styleProps.position} />
                    <Divider color="var(--color-gray-200)" />
                    <SizeSetting {...styleProps.size} />
                    <Divider color="var(--color-gray-200)" />
                    <Group label="设计">
                        <SCxStyleContainer>
                            {isMultiple && (
                                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                    <Text>方向</Text>
                                    <Controller
                                        control={control}
                                        name="direction"
                                        render={({ field }) => {
                                            return (
                                                <SegmentedControl
                                                    data={DIRECTION_OPTIONS}
                                                    style={{ width: 180 }}
                                                    fullWidth
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                />
                                            )
                                        }}
                                    />
                                </ListItem4ByecodeUi>
                            )}

                            {!isSingle && (
                                <>
                                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                        <Text>间距</Text>
                                        <Controller
                                            control={control}
                                            name="gridGap"
                                            render={({ field }) => {
                                                return (
                                                    <GapSizeInput
                                                        direction="horizontal"
                                                        value={field.value}
                                                        onChange={val => {
                                                            field.onChange(val)
                                                        }}
                                                    />
                                                )
                                            }}
                                        />
                                    </ListItem4ByecodeUi>
                                </>
                            )}
                            {isSingle && (
                                <>
                                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                        <Text>对齐</Text>
                                        <div style={{ width: 180 }}>
                                            <Controller
                                                name="align"
                                                control={control}
                                                render={({ field }) => (
                                                    <SegmentedControl
                                                        fullWidth
                                                        data={ALIGN_CONTROL}
                                                        value={field.value}
                                                        onChange={v => field.onChange(v)}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </ListItem4ByecodeUi>
                                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                        <Text>图片适应</Text>
                                        <div style={{ width: 180 }}>
                                            <Controller
                                                name="fitType"
                                                control={control}
                                                render={({ field }) => (
                                                    <SegmentedControl
                                                        data={IMAGE_FIT_OPTIONS}
                                                        fullWidth
                                                        value={field.value}
                                                        onChange={v => field.onChange(v)}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </ListItem4ByecodeUi>
                                </>
                            )}

                            {!isMultiple && (
                                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                    <Text>形状</Text>
                                    <Controller
                                        name="shape"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                style={{
                                                    width: 180
                                                }}
                                                searchable={false}
                                                disabled={isGrid}
                                                options={SHAPE_OPTIONS}
                                                value={field.value}
                                                onChange={value => {
                                                    const equalWidthAndHeight = RATIO_OPTIONS.find(item => item.value === '7')
                                                    field.onChange(value)
                                                    if (carousel) {
                                                        setValue('ratio', value === SHAPE.rectangle ? '4' : equalWidthAndHeight?.value)
                                                        return
                                                    }
                                                    setValue('ratio', value === SHAPE.rectangle ? '0' : equalWidthAndHeight?.value)
                                                }}
                                            />
                                        )}
                                    />
                                </ListItem4ByecodeUi>
                            )}

                            {shape === SHAPE.rectangle && (
                                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                    <Text>比例</Text>
                                    <Controller
                                        name="ratio"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                style={{
                                                    width: 180
                                                }}
                                                searchable={false}
                                                disabled={disabledWithVersion || shape !== SHAPE.rectangle || isGrid}
                                                options={newRatioOptions}
                                                value={field.value}
                                                onChange={field.onChange}
                                            />
                                        )}
                                    />
                                </ListItem4ByecodeUi>
                            )}
                            {(isMultiple || shape !== SHAPE.round) && (
                                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                    <Text>圆角</Text>
                                    <Controller
                                        control={control}
                                        name="radius"
                                        render={({ field }) => (
                                            <Input
                                                style={{
                                                    width: 180
                                                }}
                                                type="number"
                                                min={0}
                                                placeholder="请输入圆角"
                                                value={field.value}
                                                onChange={field.onChange}
                                            />
                                        )}
                                    />
                                </ListItem4ByecodeUi>
                            )}

                            {isGrid && (
                                <>
                                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                        <Text>图片宽度</Text>
                                        <Controller
                                            control={control}
                                            name="gridItemWidth"
                                            render={({ field }) => (
                                                <RectSizeInput
                                                    type="width"
                                                    disableAutoOption
                                                    disableFillOption
                                                    value={field.value}
                                                    onChange={val => {
                                                        field.onChange(val)
                                                    }}
                                                />
                                            )}
                                        />
                                    </ListItem4ByecodeUi>
                                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                        <Text>宫格最多显示个数</Text>
                                        <Controller
                                            control={control}
                                            name="gridShowCount"
                                            render={({ field }) => (
                                                <Input
                                                    style={{ width: 180 }}
                                                    type="number"
                                                    min={0}
                                                    max={30}
                                                    placeholder="请输入"
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                />
                                            )}
                                        />
                                    </ListItem4ByecodeUi>
                                </>
                            )}

                            {isMultiple && direction === 'horizontal' && (
                                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                    <Text>图片高度</Text>
                                    <Controller
                                        control={control}
                                        name="listItemHeight"
                                        render={({ field }) => (
                                            <RectSizeInput
                                                type="height"
                                                disableAutoOption
                                                disableFillOption
                                                value={field.value}
                                                onChange={val => {
                                                    field.onChange(val)
                                                }}
                                            />
                                        )}
                                    />
                                </ListItem4ByecodeUi>
                            )}

                            {/* <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                <Text>图片背景</Text>
                                <Controller
                                    control={control}
                                    name="background"
                                    render={({ field }) => {
                                        return (
                                            <BackgroundField
                                                enabledBackgroundTypes={[BACKGROUND_TYPE.color, BACKGROUND_TYPE.gradient]}
                                                value={field.value}
                                                onChange={field.onChange}
                                            />
                                        )
                                    }}
                                />
                            </ListItem4ByecodeUi> */}
                        </SCxStyleContainer>
                    </Group>
                    <Divider />
                    <Controller name="cursor" render={({ field }) => <CursorSetting {...field} />} />
                </>
            )}

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.CONFIGURE && (
                <>
                    <Group label="图片">
                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <Text>图片来源</Text>
                            <Controller
                                name="sourceType"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        style={{
                                            width: 180
                                        }}
                                        searchable={false}
                                        options={BLOCK_IMAGE_SOURCES}
                                        value={field.value}
                                        onChange={val => {
                                            field.onChange(val)
                                        }}
                                    />
                                )}
                            />
                        </ListItem4ByecodeUi>
                        {sourceType === IMAGE_SOURCE_TYPE.default ? (
                            <ImageUploadList blockId={blockId} disabled={disabledWithVersion} allPages={allPages} />
                        ) : (
                            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                <Text>选择变量</Text>
                                <Controller
                                    control={control}
                                    name="imageSource"
                                    render={({ field }) => (
                                        <VariableSelect
                                            innerTypes={[FIELD.innerType]}
                                            value={field.value}
                                            onChange={field.onChange}
                                            viewOptions={customViewOption ? [customViewOption] : undefined}
                                            inputOption={inputOption}
                                            pageOptions={pageOptions}
                                            userOption={userOption}
                                            field={FIELD}
                                            placeholder="粘贴图片链接"
                                            onFetchPexelsList={getPexelsList}
                                        />
                                    )}
                                />
                            </ListItem4ByecodeUi>
                        )}
                        {isSingle && (
                            <>
                                <Controller
                                    name="carousel"
                                    control={control}
                                    render={({ field }) => (
                                        <SwitchControl
                                            label="开启轮播"
                                            checked={field.value}
                                            onChange={e => {
                                                if (e.currentTarget.checked && shape === SHAPE.rectangle && ratio === '0') {
                                                    setValue('ratio', '4')
                                                }
                                                field.onChange?.(e.currentTarget.checked)
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name="noLazy"
                                    control={control}
                                    render={({ field }) => (
                                        <SwitchControl
                                            label="禁用懒加载"
                                            checked={field.value}
                                            onChange={e => field.onChange?.(e.currentTarget.checked)}
                                        />
                                    )}
                                />
                                {carousel && (
                                    <>
                                        <Controller
                                            name="autoPlay"
                                            control={control}
                                            render={({ field }) => (
                                                <SwitchControl
                                                    label="自动播放"
                                                    checked={field.value}
                                                    onChange={e => field.onChange?.(e.currentTarget.checked)}
                                                />
                                            )}
                                        />
                                        {autoPlay && (
                                            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                                <Text>停顿时间</Text>
                                                <Input
                                                    {...register('swipeSpeed')}
                                                    style={{
                                                        width: 180
                                                    }}
                                                    type="number"
                                                    min={0}
                                                    suffix={<Text>s</Text>}
                                                />
                                            </ListItem4ByecodeUi>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                        <Controller
                            name="compress"
                            control={control}
                            defaultValue
                            render={({ field }) => (
                                <SwitchControl
                                    label="开启压缩"
                                    checked={field.value}
                                    onChange={e => {
                                        field.onChange?.(e.currentTarget.checked)
                                    }}
                                />
                            )}
                        />
                        <Controller
                            name="isPreview"
                            control={control}
                            render={({ field }) => (
                                <SwitchControl
                                    label="图片预览"
                                    checked={field.value}
                                    onChange={e => {
                                        field.onChange?.(e.currentTarget.checked)
                                    }}
                                />
                            )}
                        />
                    </Group>
                    <Divider color="var(--color-gray-200)" />
                    <AnchorSetting />
                    <Divider />
                    <VisibilitySetting />
                </>
            )}

            <div key={BLOCK_SETTINGS_SEGMENTED_ENUM.INTERACTION} hidden={currentTab !== BLOCK_SETTINGS_SEGMENTED_ENUM.INTERACTION}>
                <Controller
                    name="eventList"
                    control={control}
                    render={({ field }) => <EventList blockType="image" value={field.value} onChange={field.onChange} isImage />}
                />
                <Divider color="var(--color-gray-200)" />
                <AnimationSetting />
            </div>
        </>
    )
}
